"use client";

import { useCallback, useEffect, useState } from "react";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { FactfindDefaultsRule } from "@/services/api/agentic-v2/types";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog/Dialog";

export type FactfindDefaultsDialogProps = {
  open: boolean;
  onClose: () => void;
};

/** ONB-05-01 admin defaults editor, opened from the demo dropdown. */
export function FactfindDefaultsDialog({ open, onClose }: FactfindDefaultsDialogProps) {
  const [rules, setRules] = useState<FactfindDefaultsRule[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [newBu, setNewBu] = useState("");
  const [newSegment, setNewSegment] = useState("");
  const [newAdviser, setNewAdviser] = useState("");

  useEffect(() => {
    if (!open) return;
    setError(null);
    void AgenticV2API.getFactfindDefaults()
      .then((result) => setRules(result.rules))
      .catch(() => setError("Couldn't load Factfind defaults"));
  }, [open]);

  const saveRules = useCallback(async (next: FactfindDefaultsRule[]) => {
    setSaving(true);
    setError(null);
    try {
      const result = await AgenticV2API.putFactfindDefaults(next);
      setRules(result.rules);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Couldn't save defaults");
    } finally {
      setSaving(false);
    }
  }, []);

  const addRule = useCallback(() => {
    void saveRules([
      ...rules,
      {
        business_unit: newBu.trim() || null,
        segment: newSegment.trim() || null,
        adviser_id: newAdviser.trim() || null,
        invitation_pre_tick: true,
        questionnaire_type: null,
      },
    ]).then(() => {
      setNewBu("");
      setNewSegment("");
      setNewAdviser("");
    });
  }, [newAdviser, newBu, newSegment, rules, saveRules]);

  return (
    <Dialog open={open} onClose={onClose} size="lg" pinToViewport>
      <DialogTitle>Factfind admin defaults</DialogTitle>
      <DialogDescription>
        Company send-via-email rules by business unit, segment, and adviser. Requires an admin user.
      </DialogDescription>
      <DialogBody>
        <div
          className="flex flex-col gap-2 rounded-md border border-slate-200 p-2 dark:border-slate-700"
          data-testid="factfind-defaults-editor"
        >
          {rules.length === 0 ? (
            <p className="text-[11px] text-slate-500">No rules — the invitation is sent via email.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {rules.map((rule, index) => (
                <li
                  key={index}
                  className="flex flex-wrap items-center gap-2 text-[11px]"
                  data-testid={`factfind-default-rule-${index}`}
                >
                  <span>
                    {[rule.business_unit ?? "any BU", rule.segment ?? "any segment", rule.adviser_id ?? "any adviser"].join(" · ")}
                  </span>
                  <span>{rule.invitation_pre_tick ? "send via email" : "don't send via email"}</span>
                  <Button
                    size="sm"
                    plain
                    disabled={saving}
                    onClick={() => void saveRules(rules.filter((_, row) => row !== index))}
                    data-testid={`factfind-default-delete-${index}`}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <FormInput value={newBu} onChange={(e) => setNewBu(e.target.value)} placeholder="Business unit (optional)" aria-label="Business unit" />
            <FormInput value={newSegment} onChange={(e) => setNewSegment(e.target.value)} placeholder="Segment (optional)" aria-label="Segment" />
            <FormInput value={newAdviser} onChange={(e) => setNewAdviser(e.target.value)} placeholder="Adviser id (optional)" aria-label="Adviser id" />
            <Button size="sm" plain disabled={saving} onClick={addRule} data-testid="factfind-default-add">
              Add rule
            </Button>
          </div>
          {error ? (
            <p className="text-[11px] text-red-600" data-testid="factfind-defaults-error">
              {error}
            </p>
          ) : null}
        </div>
      </DialogBody>
      <DialogActions>
        <Button type="button" plain onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
